/**
 * Page-loop helper for finance Excel export (#178).
 *
 * The three surfaces paginate their list APIs; this module walks them
 * until the filtered range is in memory or the hard cap is crossed.
 * Crossing the cap aborts — we never silently truncate a spreadsheet
 * a finance reader would treat as complete. Backend streaming export
 * is a later slice.
 */

export const EXPORT_ROW_CAP = 200_000;

export type CollectOutcome<T> =
  | { readonly status: 'ok'; readonly rows: readonly T[] }
  | { readonly status: 'cap' };

export interface FetchPageResult<T> {
  readonly rows: readonly T[];
  /** Known list length, or `null` when the endpoint has no total. */
  readonly total: number | null;
}

/**
 * Pull every page of a filtered list, stopping at `cap`.
 *
 * A first page that already reports `total > cap` returns `cap`
 * without walking further. An endpoint without `total` is treated as
 * over-cap the moment appending the next page would pass the limit.
 */
export async function collectPagedRows<T>(options: {
  readonly fetchPage: (offset: number, limit: number) => Promise<FetchPageResult<T>>;
  readonly pageSize: number;
  readonly cap?: number;
  /** Rows accepted so far. Fired after each page that fits under the cap. */
  readonly onProgress?: (rows: number) => void;
}): Promise<CollectOutcome<T>> {
  const cap = options.cap ?? EXPORT_ROW_CAP;
  const pageSize = Math.max(1, options.pageSize);
  const collected: T[] = [];
  let offset = 0;
  let total: number | null = null;
  const maxPages = Math.ceil(cap / pageSize) + 1;

  for (let page = 0; page < maxPages; page += 1) {
    const result = await options.fetchPage(offset, pageSize);
    if (page === 0 && result.total !== null && result.total > cap) {
      return { status: 'cap' };
    }
    if (result.total !== null) {
      total = result.total;
    }
    if (collected.length + result.rows.length > cap) {
      return { status: 'cap' };
    }
    collected.push(...result.rows);
    options.onProgress?.(collected.length);
    if (result.rows.length < pageSize) {
      return { status: 'ok', rows: collected };
    }
    if (total !== null && collected.length >= total) {
      return { status: 'ok', rows: collected };
    }
    offset += pageSize;
  }
  return { status: 'cap' };
}
